import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ComponentProps, memo, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';

import ItemCard from '@/components/GridItemCard/Card/Card';
import TimedOrCategorySearchCard, {
    CarouselTimedOrCategorySearchCard,
} from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import SideScroller from '@/components/SideScroller/SideScroller';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { getRecommendationTypeFromTitle } from '@/utils/amplitude/analytics/item/recommendationTypes';
import noop from '@/utils/noop';

import FluidItemGrid from '../GridItemCard/FluidItemGrid/FluidItemGrid';
import { RouteButton } from '../RouteButton/RouteButton';

const GRID_CHANGE_WIDTH = 550;
const MAX_ITEMS_FOR_MOBILE = 6;
const VISIBILITY_THRESHOLD = 0.5;

type Props = {
    allowBidding?: boolean;
    className?: string;
    cta?: boolean;
    hideLiveButton?: boolean;
    identifier: string;
    itemIds: number[];
    links?: ReactNode;
    onVisibleItemIdsChange?: (visibleItemIds: number[]) => void;
    seeAllUrl?: string;
    showBidStatus?: boolean;
    showSoldBadge?: boolean;
    testid?: string;
    title: string;
    trackItemClick?: Function;
} & Partial<ComponentProps<typeof Carousel>>;

const ItemShelf = (props: Props) => {
    const {
        className = '',
        cta = false,
        identifier,
        itemIds = [],
        links = false,
        onVisibleItemIdsChange,
        seeAllUrl = '',
        testid,
        title,
        trackItemClick = noop,
        ...carouselProps
    } = props;
    const wide = useIsWiderThan(GRID_CHANGE_WIDTH);
    const { formatMessage } = useIntl();
    const recommendationType = getRecommendationTypeFromTitle(title);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onVisibleItemIdsChange) {
            return;
        }

        if (!wide) {
            onVisibleItemIdsChange(itemIds.slice(0, Math.min(itemIds.length, MAX_ITEMS_FOR_MOBILE)));
            return;
        }

        const container = containerRef.current;
        const scrollContainer = container?.querySelector('[role="list"]') as HTMLElement | null;
        if (!scrollContainer) {
            return;
        }

        const listItems = scrollContainer.querySelectorAll(':scope > [role="listitem"]');
        if (listItems.length === 0) {
            return;
        }

        const visibleIndices = new Set<number>();

        const updateVisibleIds = () => {
            const ids = Array.from(visibleIndices)
                .sort((a, b) => a - b)
                .map((i) => itemIds[i])
                .filter((id) => typeof id === 'number');
            onVisibleItemIdsChange(ids);
        };

        const listItemsArray = Array.from(listItems);

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const index = listItemsArray.indexOf(entry.target);
                    if (index === -1) {
                        continue;
                    }
                    if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
                        visibleIndices.add(index);
                    } else {
                        visibleIndices.delete(index);
                    }
                }
                updateVisibleIds();
            },
            { root: scrollContainer, threshold: VISIBILITY_THRESHOLD }
        );

        listItemsArray.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [wide, itemIds, onVisibleItemIdsChange]);

    const trackClick = useCallback(
        (itemId) => {
            trackItemClick(itemId);
        },
        [trackItemClick]
    );

    const carouselCards = useMemo(
        () =>
            itemIds?.map((itemId, index) => ({
                card: (
                    <ItemCard
                        itemCardOption={CarouselTimedOrCategorySearchCard}
                        itemId={itemId}
                        key={`${index}-${identifier}`}
                        recommendationPosition={index + 1}
                        recommendationType={recommendationType}
                        trackClick={() => trackClick(itemId)}
                    />
                ),
                key: `${index}-${identifier}`,
            })),
        [identifier, itemIds, recommendationType, trackClick]
    );

    if (!itemIds.length) {
        return (
            <div
                className={cn(
                    'flex h-[361px] w-full max-w-[1200px] items-center justify-center bg-background-subdued',
                    className
                )}
                id={identifier}
            >
                <Throbber />
            </div>
        );
    }

    const fullCarouselHeader = {
        ...(seeAllUrl && {
            action: {
                path: seeAllUrl,
                text: formatMessage({ id: 'see_all' }),
            },
        }),
        title,
    };

    return (
        <div
            className={cn('flex flex-col bg-background-primary transition-all duration-200 ease-in', className)}
            data-testid={testid}
            id={identifier}
            ref={containerRef}
        >
            {Boolean(links) && links}
            {wide ? (
                <SideScroller
                    cards={carouselCards}
                    className="md:w-[calc(100%-20px)] md:ml-[20px] w-full max-w-[1200px] self-center"
                    useOverlayControls
                    {...carouselProps}
                    {...(!links && fullCarouselHeader)}
                />
            ) : (
                <>
                    {!links && <H2 className="ml-[20px] xsMax:ml-0">{title}</H2>}
                    <FluidItemGrid
                        className="mb-[35px] px-[20px]"
                        itemCardOption={TimedOrCategorySearchCard}
                        itemIds={itemIds.slice(0, MAX_ITEMS_FOR_MOBILE)}
                        recommendationType={recommendationType}
                    />
                    {cta && (
                        <div className="mx-auto text-center">
                            <RouteButton
                                className="w-[250px] max-w-[calc(100%-40px)]"
                                to={seeAllUrl}
                            >
                                {formatMessage({ id: 'see_all' })}
                            </RouteButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default memo<Props>(ItemShelf, isEqual);
